/**
 * Componentes do template.
 *
 * Cada função devolve um fragmento de HTML e recebe dados **já validados** pelo schema do domínio.
 * Todo valor dinâmico passa por `escaparHtml` ou `atributo` — ver `escapar.ts`.
 *
 * Referência visual: `docs/design-system.md`, seções 7 a 8.
 */

import type { DocumentoProposta, ResponsavelCongelado } from '@emaster/dominio';

import { atributo, codificarUrl, escaparHtml } from './escapar.ts';
import { CONTAINER, FALLBACK_IMAGEM, icone, ASSETS } from './tokens.ts';

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/** `2026-08-04` para `4 de agosto de 2026`. Sem `Date`, para não depender de fuso. */
export function dataLonga(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  const indice = Number(mes) - 1;
  return `${Number(dia)} de ${MESES[indice] ?? mes} de ${ano}`;
}

/** `2026-08-04` para `04/08/2026`. */
export function dataCurta(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

// ---------------------------------------------------------------------------
// Navegação
// ---------------------------------------------------------------------------

export interface ItemNavegacao {
  readonly ancora: string;
  readonly rotulo: string;
}

/** Nav fixa. Marcada `no-print` porque não faz sentido no documento impresso. */
export function navegacao(itens: readonly ItemNavegacao[]): string {
  const links = itens
    .map(
      (i) =>
        `<a href=${atributo(`#${i.ancora}`)} class="text-sm font-semibold text-gray-600 hover:text-brand-accent transition-colors">${escaparHtml(i.rotulo)}</a>`,
    )
    .join('\n                    ');

  return `
    <nav class="fixed w-full z-50 glass-card border-b border-gray-200 transition-all duration-300 no-print">
        <div class="${CONTAINER}">
            <div class="flex justify-between items-center h-20">
                <div class="flex items-center gap-4">
                    <img class="h-10 w-auto" src=${atributo(ASSETS.logoEmaster)} alt="eMaster Cloud" ${FALLBACK_IMAGEM}>
                </div>
                <div class="hidden lg:flex items-center space-x-7">
                    ${links}
                </div>
                <div class="flex items-center">
                    <img class="h-9 w-auto mr-4 hidden sm:block" src=${atributo(ASSETS.logoAws)} alt="AWS Advanced Tier Services Partner" ${FALLBACK_IMAGEM}>
                    <a href="#contato" class="bg-brand-accent hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center gap-2">
                        ${icone('phone', 'w-4 h-4')} Falar com Especialista
                    </a>
                </div>
            </div>
        </div>
    </nav>`;
}

// ---------------------------------------------------------------------------
// Herói
// ---------------------------------------------------------------------------

/**
 * Herói.
 *
 * O H1 segue a fórmula obrigatória `Para a [CLIENTE]: [transformação]`, com o nome do cliente em
 * gradiente. H1 genérico é o anti-padrão 15.
 *
 * @param ancoraInicio Âncora do primeiro bloco da narrativa, destino do botão "Conhecer a
 * proposta". Recebida de quem monta a página em vez de fixada aqui: as seções de conteúdo são
 * editoriais, então a âncora que existe num documento pode não existir no próximo. Fixar `#contexto`
 * funcionava só nas propostas em que o vendedor por acaso nomeava a primeira seção assim — nas
 * demais o botão não saía do lugar, sem erro nenhum no console.
 */
export function heroi(doc: DocumentoProposta, ancoraInicio: string): string {
  const cliente = escaparHtml(doc.cliente);
  const promessa = escaparHtml(doc.conteudo.promessaValor);

  return `
    <section class="hero-pattern pt-32 pb-20 lg:pt-40 lg:pb-28 relative overflow-hidden">
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none opacity-20">
            <div class="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-accent blur-3xl"></div>
            <div class="absolute top-1/2 -left-24 w-72 h-72 rounded-full bg-blue-500 blur-3xl"></div>
        </div>
        <div class="${CONTAINER} relative z-10">
            <div class="text-center max-w-4xl mx-auto">
                <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-brand-accent text-sm font-semibold mb-6">
                    <span class="flex h-2 w-2 relative">
                      <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-75"></span>
                      <span class="relative inline-flex rounded-full h-2 w-2 bg-brand-accent"></span>
                    </span>
                    ${escaparHtml(doc.conteudo.badgeContexto)}
                </div>
                <h1 class="text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
                    Para a <span class="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-orange-400">${cliente}</span>: ${promessa}
                </h1>
                <p class="text-lg lg:text-xl text-gray-300 mb-10 max-w-3xl mx-auto font-light">${escaparHtml(doc.conteudo.subtituloHero)}</p>
                <div class="flex flex-col sm:flex-row justify-center gap-4">
                    <a href=${atributo(`#${ancoraInicio}`)} class="bg-white text-brand-blue hover:bg-gray-50 px-8 py-3.5 rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center gap-2">
                        Conhecer a proposta ${icone('arrow-right', 'w-4 h-4')}
                    </a>
                    <a href="#investimento" class="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                        Ver Investimento ${icone('wallet', 'w-4 h-4')}
                    </a>
                </div>
                <p class="text-xs text-gray-400 mt-8">Proposta ${escaparHtml(doc.numero)} &middot; emitida em ${dataLonga(doc.emitidaEm)} &middot; Válida até ${dataCurta(doc.validaAte)}</p>
            </div>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Cabeçalho de seção
// ---------------------------------------------------------------------------

/** Eyebrow + H2 + barra + parágrafo de contexto. Padrão de toda seção. */
export function cabecalhoSecao(
  eyebrow: string,
  titulo: string,
  paragrafos: readonly string[] = [],
): string {
  const corpo = paragrafos
    .map((p) => `<p class="text-gray-600 text-lg leading-relaxed mt-6">${escaparHtml(p)}</p>`)
    .join('\n            ');

  return `<div class="mb-12 max-w-3xl">
            <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">${escaparHtml(eyebrow)}</span>
            <h2 class="text-3xl font-bold text-brand-blue mt-2 mb-4">${escaparHtml(titulo)}</h2>
            <div class="w-20 h-1.5 bg-brand-accent rounded-full"></div>
            ${corpo}
        </div>`;
}

/**
 * Seção de conteúdo editorial.
 *
 * `scroll-mt-24` é obrigatório em toda seção com âncora, para compensar a nav fixa (anti-padrão 16).
 */
export function secaoTexto(
  ancora: string,
  eyebrow: string,
  titulo: string,
  paragrafos: readonly string[],
  fundo: 'branco' | 'cinza',
): string {
  const classeFundo = fundo === 'branco' ? 'bg-white' : 'bg-gray-50';
  return `
    <section id=${atributo(ancora)} class="scroll-mt-24 py-20 ${classeFundo} border-t border-gray-200">
        <div class="${CONTAINER}">
            ${cabecalhoSecao(eyebrow, titulo, paragrafos)}
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Escopo
// ---------------------------------------------------------------------------

export interface ItemEscopo {
  readonly texto: string;
  readonly destaque: string | null;
}

function listaEscopo(itens: readonly ItemEscopo[], positiva: boolean): string {
  const iconeItem = positiva ? 'check' : 'x';
  const cor = positiva ? 'text-green-600' : 'text-red-500';

  return itens
    .map((i) => {
      const destaque =
        i.destaque === null
          ? ''
          : ` <strong class="text-brand-blue">${escaparHtml(i.destaque)}</strong>`;
      return `<li class="flex items-start gap-3">${icone(iconeItem, `w-5 h-5 ${cor} shrink-0 mt-0.5`)}<span class="text-gray-600 text-sm leading-relaxed">${escaparHtml(i.texto)}${destaque}</span></li>`;
    })
    .join('\n                        ');
}

export function secaoEscopo(
  incluido: readonly ItemEscopo[],
  excluido: readonly ItemEscopo[],
): string {
  if (incluido.length === 0 && excluido.length === 0) return '';

  return `
    <section id="escopo" class="scroll-mt-24 py-20 bg-gray-50 border-t border-gray-200">
        <div class="${CONTAINER}">
            ${cabecalhoSecao('Escopo', 'O que está incluído e o que fica fora')}
            <div class="grid lg:grid-cols-2 gap-6">
                <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-11 h-11 shrink-0 bg-green-50 rounded-xl flex items-center justify-center text-green-600">${icone('check-circle-2', 'w-6 h-6')}</div>
                        <h3 class="text-xl font-bold text-brand-blue">Incluído</h3>
                    </div>
                    <ul class="space-y-4">
                        ${listaEscopo(incluido, true)}
                    </ul>
                </div>
                <div class="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-11 h-11 shrink-0 bg-red-50 rounded-xl flex items-center justify-center text-red-500">${icone('x-circle', 'w-6 h-6')}</div>
                        <h3 class="text-xl font-bold text-brand-blue">Fora do escopo</h3>
                    </div>
                    <ul class="space-y-4">
                        ${listaEscopo(excluido, false)}
                    </ul>
                </div>
            </div>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Próximos passos e condições
// ---------------------------------------------------------------------------

export interface PassoCronograma {
  readonly titulo: string;
  readonly descricao: string;
  readonly duracao: string | null;
}

export interface CondicaoComercial {
  readonly titulo: string;
  readonly descricao: string;
  readonly icone: string;
}

export function secaoProximosPassos(
  passos: readonly PassoCronograma[],
  condicoes: readonly CondicaoComercial[],
): string {
  if (passos.length === 0 && condicoes.length === 0) return '';

  const listaPassos = passos
    .map((p, indice) => {
      const ultimo = indice === passos.length - 1;
      const caixa = ultimo
        ? 'bg-brand-blue rounded-2xl p-6 border-2 border-brand-accent shadow-lg'
        : 'bg-white rounded-2xl p-6 border border-gray-100 shadow-sm';
      const corTitulo = ultimo ? 'text-white' : 'text-brand-blue';
      const corTexto = ultimo ? 'text-gray-300' : 'text-gray-500';
      const duracao =
        p.duracao === null
          ? ''
          : `<span class="text-xs font-semibold ${ultimo ? 'text-brand-accent' : 'text-brand-accent'} block mt-1">${escaparHtml(p.duracao)}</span>`;

      return `<div class="${caixa} flex flex-col sm:flex-row gap-5">
                    <div class="flex items-center gap-4 sm:w-64 shrink-0">
                        <span class="w-10 h-10 shrink-0 rounded-full bg-brand-accent text-white font-bold flex items-center justify-center text-sm">${indice + 1}</span>
                        <p class="font-bold ${corTitulo} leading-tight">${escaparHtml(p.titulo)}${duracao}</p>
                    </div>
                    <p class="text-sm ${corTexto} leading-relaxed">${escaparHtml(p.descricao)}</p>
                </div>`;
    })
    .join('\n                ');

  const listaCondicoes = condicoes
    .map(
      (c) => `<div class="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <div class="w-11 h-11 bg-white rounded-xl flex items-center justify-center text-brand-blue mb-4">${icone(c.icone)}</div>
                    <h4 class="font-bold text-brand-blue mb-2">${escaparHtml(c.titulo)}</h4>
                    <p class="text-sm text-gray-500 leading-relaxed">${escaparHtml(c.descricao)}</p>
                </div>`,
    )
    .join('\n                ');

  const blocoCondicoes =
    condicoes.length === 0
      ? ''
      : `<div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                ${listaCondicoes}
            </div>`;

  return `
    <section id="proximos-passos" class="scroll-mt-24 py-20 bg-white border-t border-gray-200">
        <div class="${CONTAINER}">
            ${cabecalhoSecao('Próximos Passos', 'Como seguimos')}
            <div class="space-y-4 mb-12">
                ${listaPassos}
            </div>
            ${blocoCondicoes}
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Contato
// ---------------------------------------------------------------------------

/**
 * Card de contato do responsável.
 *
 * Todo `target="_blank"` recebe `rel="noopener"` (anti-padrão 7), o CTA de aprovação é `<a href>` e
 * nunca `<button>` sem handler (anti-padrão 4), e o assunto do e-mail é URL-encoded.
 */
export function secaoContato(responsavel: ResponsavelCongelado, cliente: string): string {
  const assunto = codificarUrl(`Proposta Comercial - ${cliente}`);
  const wa = `https://wa.me/${responsavel.whatsapp}?text=${codificarUrl(`Olá ${responsavel.nomeExibicao}! Gostaria de falar sobre a proposta comercial de ${cliente}.`)}`;
  const foto =
    responsavel.fotoUrl === null
      ? ''
      : `<img src=${atributo(responsavel.fotoUrl)} alt=${atributo(responsavel.nomeExibicao)} class="w-16 h-16 rounded-full object-cover border-2 border-brand-accent flex-shrink-0" ${FALLBACK_IMAGEM}>`;

  return `
    <section id="contato" class="scroll-mt-24 py-20 bg-gray-900 text-white border-t border-gray-800">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span class="text-sm font-semibold text-brand-accent uppercase tracking-wider">Contato</span>
            <h2 class="text-3xl font-bold text-white mt-2 mb-4">Vamos alinhar os próximos passos?</h2>
            <div class="w-20 h-1.5 bg-brand-accent rounded-full mx-auto"></div>
            <p class="text-gray-400 text-lg leading-relaxed mt-6 mb-12">
                Fale diretamente com o responsável por esta proposta para revisar os números, ajustar o escopo e definir o cronograma.
            </p>
            <div class="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div class="flex flex-col md:flex-row items-center gap-8">
                    <!-- Info do vendedor -->
                    <div class="flex items-center gap-5 flex-1">
                        ${foto}
                        <div class="text-left">
                            <h4 class="text-xl font-bold text-white">${escaparHtml(responsavel.nomeExibicao)}</h4>
                            <p class="text-brand-accent text-sm font-semibold">${escaparHtml(responsavel.cargoExibicao)}</p>
                            <p class="text-gray-400 text-xs mt-1">${escaparHtml(responsavel.email)}</p>
                        </div>
                    </div>

                    <!-- CTAs -->
                    <div class="grid grid-cols-2 gap-3 w-full md:w-auto">
                        <a href=${atributo(wa)} target="_blank" rel="noopener"
                           class="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-6 py-3.5 rounded-lg font-semibold text-base transition-colors">
                            ${icone('message-circle', 'w-5 h-5')} WhatsApp
                        </a>
                        <a href=${atributo(`mailto:${responsavel.email}?subject=${assunto}`)}
                           class="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3.5 rounded-lg font-semibold text-base transition-colors">
                            ${icone('mail', 'w-5 h-5')} E-mail
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// Avaliação (NPS)
// ---------------------------------------------------------------------------

/**
 * Bloco de avaliação de satisfação (NPS).
 *
 * POR QUÊ fica abaixo do CTA do vendedor: a opinião sobre o atendimento só faz sentido depois que
 * o cliente leu a proposta e viu os canais de contato — pedir avaliação antes seria prematuro.
 *
 * POR QUÊ é mais discreto que os CTAs de WhatsApp e e-mail: aqueles convertem venda e são a ação
 * principal da página; este coleta opinião e é ação secundária. O botão usa borda em vez de
 * preenchimento sólido para refletir essa hierarquia visual sem competir com os CTAs de venda.
 *
 * Os parâmetros `seller` e `company` pré-preenchem o formulário no destino, eliminando a etapa de
 * identificação e aumentando a taxa de resposta — o cliente não precisa digitar quem o atendeu.
 */
export function secaoAvaliacao(responsavel: ResponsavelCongelado, cliente: string): string {
  const url = `https://avalia.emaster.cloud/vendas?seller=${codificarUrl(responsavel.nomeExibicao)}&company=${codificarUrl(cliente)}`;

  return `
    <div role="region" aria-label="Avaliação de satisfação" class="bg-gray-900 pb-20 pt-4">
        <div class="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="border-t border-gray-700 pt-12 text-center">
                <div class="mb-4">${icone('star', 'w-8 h-8 text-brand-accent mx-auto')}</div>
                <h3 class="text-xl font-bold text-white mb-2">Como foi sua experiência?</h3>
                <p class="text-gray-400 text-sm mb-8">Sua opinião nos ajuda a melhorar continuamente nossos serviços.</p>
                <a href=${atributo(url)} target="_blank" rel="noopener"
                   aria-label="Avaliar proposta e atendimento no sistema de satisfação da eMaster (abre em nova aba)"
                   class="inline-flex items-center gap-2 border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white px-7 py-3 rounded-lg font-semibold text-sm transition-colors">
                    ${icone('message-circle', 'w-4 h-4')} Avaliar Proposta e Atendimento
                </a>
                <p class="text-gray-500 text-xs mt-6">Leva menos de 2 minutos • Sua resposta é confidencial</p>
            </div>
        </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Rodapé
// ---------------------------------------------------------------------------

/** Razão social padronizada: eMaster Cloud & Security, nunca "eMaster Cloud" isolado. */
export function rodape(doc: DocumentoProposta): string {
  const ano = doc.emitidaEm.slice(0, 4);

  return `
    <footer class="bg-black text-gray-500 py-8 border-t border-gray-800">
        <div class="${CONTAINER} flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left text-sm">
            <div class="flex items-center justify-center md:justify-start gap-4">
                <img class="h-8 w-auto grayscale opacity-70" src=${atributo(ASSETS.logoEmaster)} alt="eMaster Cloud" ${FALLBACK_IMAGEM}>
                <div class="h-4 w-px bg-gray-700 hidden md:block"></div>
                <div>
                    <p>&copy; ${escaparHtml(ano)} eMaster Cloud &amp; Security. Todos os direitos reservados.</p>
                    <p class="mt-1 text-xs text-gray-600">Proposta ${escaparHtml(doc.numero)} &mdash; documento comercial confidencial para ${escaparHtml(doc.cliente)}. Válida até ${dataCurta(doc.validaAte)}.</p>
                </div>
            </div>
            <div class="flex items-center justify-center gap-2 opacity-60">
                ${icone('cloud', 'w-6 h-6')} AWS Advanced Tier Services Partner
            </div>
        </div>
    </footer>`;
}

/**
 * Interceptador de cliques em âncoras internas.
 *
 * Dentro de `<iframe srcdoc>`, links `#ancora` fazem o iframe navegar para a URL do host, carregando
 * o painel dentro da prévia. Este script intercepta o clique, previne a navegação nativa e faz
 * scroll suave manual.
 */
export function scriptNavegacaoInterna(): string {
  return `
        (function () {
            document.addEventListener('click', function (e) {
                var alvo = e.target;
                while (alvo && alvo.tagName !== 'A') { alvo = alvo.parentElement; }
                if (!alvo) return;
                var href = alvo.getAttribute('href');
                if (!href || href.charAt(0) !== '#') return;
                e.preventDefault();
                var id = href.slice(1);
                var destino = document.getElementById(id);
                if (destino) { destino.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
            }, true);
        })();`;
}

/** Botão flutuante de impressão. Não aparece no documento impresso. */
export function botaoImprimir(): string {
  return `
    <div class="fixed bottom-6 right-6 z-50 no-print">
        <button type="button" data-print-document class="bg-brand-blue/70 hover:bg-brand-blue/90 backdrop-blur-sm text-white font-semibold py-3 px-5 rounded-xl flex items-center gap-2 shadow-lg transition-all">
            ${icone('printer', 'w-5 h-5')} Imprimir / Salvar PDF
        </button>
    </div>`;
}
